import { CheckCircle, Zap, Rocket, Target } from 'lucide-react';
import logoImg from '@/assets/images/logo.png';

const timeline = [
  {
    month: 'August 2025',
    title: 'GameLens Founded',
    description: 'Born from one question: why does a stadium match get a broadcast, and a Saturday turf match gets nothing?',
    icon: Target,
    color: '#FF4F7B',
  },
  {
    month: 'October 2025',
    title: 'Built CricAI\'s AI Model',
    description: 'Built CricAI\'s AI model from scratch in India, training it on 1,000+ hours of real box cricket footage and 10 lakh+ frames.',
    icon: Zap,
    color: '#FF9A3C',
  },
  {
    month: 'March 2026',
    title: 'First Pilot Launch',
    description: 'First pilot turf went live in Mumbai, running free installs to learn what turf owners and players actually needed.',
    icon: Rocket,
    color: '#4FC3F7',
  },
  {
    month: 'Today',
    title: 'CricAI is Live',
    description: 'AI cameras mounted over the turf, full match recording, and 1-tap auto-edited highlights, on the App Store and Google Play.',
    icon: CheckCircle,
    color: '#22C55E',
    highlight: true,
  },
  {
    month: 'Next',
    title: 'Live Ball-by-Ball AI Scoring',
    description: 'Live ball-by-ball AI scoring rolling out, free for every turf already on board — then pickleball, football, and more.',
    icon: Rocket,
    color: '#6366F1',
    future: true,
  },
];

export default function JourneyTimeline() {
  return (
    <section id="journey" data-testid="journey-section" className="py-12 md:py-16 px-6 md:px-12 lg:px-24 bg-gl-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] rounded-full bg-gl-blue/5 blur-[150px]" />
      <div className="absolute bottom-1/4 -left-40 w-[500px] h-[500px] rounded-full bg-gl-orange/5 blur-[150px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 gsap-heading">
          <div className="flex justify-center mb-6">
            <img src={logoImg} alt="GameLens Logo" className="h-12 md:h-14 object-contain" />
          </div>
          <span className="text-xs font-body font-semibold uppercase tracking-[0.2em] text-gl-orange/80">Our Journey</span>
          <h2 data-testid="journey-heading" className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight tracking-tight mt-4">
            From a Question to<br /><span className="text-gradient-orange">Reality.</span>
          </h2>
          <p className="mt-4 text-base font-body text-white/45 max-w-2xl mx-auto leading-relaxed">
            GameLens is on a mission to bring professional-grade AI officiating to every turf, academy, and stadium across the world.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative max-w-4xl mx-auto">
          {/* Timeline center line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-gl-orange via-gl-blue to-gl-orange/20 -translate-x-1/2" />

          {/* Timeline items */}
          <div className="space-y-8 md:space-y-12">
            {timeline.map((item, index) => {
              const IconComponent = item.icon;
              const isEven = index % 2 === 0;
              const isCurrent = item.highlight;
              const isFuture = item.future;

              return (
                <div key={index} data-testid={`journey-item-${index}`} className="relative">
                  {/* Desktop alternating layout */}
                  <div className="hidden md:flex md:items-center md:gap-12">
                    {isEven ? (
                      <>
                        {/* Left content */}
                        <div className="flex-1 text-right">
                          <div className={`rounded-2xl p-6 transition-all duration-500 ${isCurrent ? 'dark-glass border border-white/20' : 'dark-glass'}`}
                            style={isCurrent ? {
                              backgroundColor: `${item.color}08`,
                              borderColor: `${item.color}40`,
                              boxShadow: `0 0 40px ${item.color}15`
                            } : {}}>
                            <span className="text-xs font-body font-semibold uppercase tracking-wider text-white/40">{item.month}</span>
                            <h3 className="font-heading font-bold text-xl text-white mt-2">{item.title}</h3>
                            <p className="text-sm font-body text-white/50 mt-2 leading-relaxed">{item.description}</p>
                          </div>
                        </div>

                        {/* Center icon */}
                        <div className="flex-shrink-0">
                          <div className="relative flex items-center justify-center">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${isCurrent ? 'ring-4 ring-offset-4 ring-offset-gl-black scale-125' : ''}`}
                              style={{
                                background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}05 100%)`,
                                borderColor: `${item.color}40`,
                                boxShadow: isCurrent ? `0 0 30px ${item.color}40` : `0 0 15px ${item.color}20`,
                              }}>
                              <IconComponent className="w-7 h-7" style={{ color: item.color }} />
                            </div>
                            {isCurrent && (
                              <div className="absolute inset-0 rounded-full animate-pulse" style={{
                                background: `radial-gradient(circle, ${item.color}40 0%, transparent 70%)`,
                              }} />
                            )}
                          </div>
                        </div>

                        {/* Right empty space */}
                        <div className="flex-1" />
                      </>
                    ) : (
                      <>
                        {/* Left empty space */}
                        <div className="flex-1" />

                        {/* Center icon */}
                        <div className="flex-shrink-0">
                          <div className="relative flex items-center justify-center">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${isCurrent ? 'ring-4 ring-offset-4 ring-offset-gl-black scale-125' : ''}`}
                              style={{
                                background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}05 100%)`,
                                borderColor: `${item.color}40`,
                                boxShadow: isCurrent ? `0 0 30px ${item.color}40` : `0 0 15px ${item.color}20`,
                              }}>
                              <IconComponent className="w-7 h-7" style={{ color: item.color }} />
                            </div>
                            {isCurrent && (
                              <div className="absolute inset-0 rounded-full animate-pulse" style={{
                                background: `radial-gradient(circle, ${item.color}40 0%, transparent 70%)`,
                              }} />
                            )}
                          </div>
                        </div>

                        {/* Right content */}
                        <div className="flex-1 text-left">
                          <div className={`rounded-2xl p-6 transition-all duration-500 ${isCurrent ? 'dark-glass border border-white/20' : 'dark-glass'}`}
                            style={isCurrent ? {
                              backgroundColor: `${item.color}08`,
                              borderColor: `${item.color}40`,
                              boxShadow: `0 0 40px ${item.color}15`
                            } : {}}>
                            <span className="text-xs font-body font-semibold uppercase tracking-wider text-white/40">{item.month}</span>
                            <h3 className="font-heading font-bold text-xl text-white mt-2">{item.title}</h3>
                            <p className="text-sm font-body text-white/50 mt-2 leading-relaxed">{item.description}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Mobile layout */}
                  <div className="md:hidden flex gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${item.color}30 0%, ${item.color}10 100%)`,
                          boxShadow: `0 0 20px ${item.color}30`,
                        }}>
                        <IconComponent className="w-5 h-5" style={{ color: item.color }} />
                      </div>
                      {/* Mobile timeline line */}
                      {index < timeline.length - 1 && (
                        <div className="w-1 h-16 bg-gradient-to-b from-white/20 to-transparent ml-[22px] mt-2" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-8">
                      <span className="text-xs font-body font-semibold uppercase tracking-wider text-white/40">{item.month}</span>
                      <h3 className="font-heading font-bold text-lg text-white mt-1">{item.title}</h3>
                      <p className="text-sm font-body text-white/50 mt-2 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center gsap-scale">
          <h3 className="font-heading font-bold text-2xl text-white mb-4">Be Part of the Journey</h3>
          <p className="text-base font-body text-white/45 max-w-xl mx-auto mb-8 leading-relaxed">
            Join thousands of turfs, academies, and players already using GameLens. Your match deserves to be broadcast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-3 bg-gradient-brand text-white rounded-xl text-sm font-body font-semibold hover:opacity-90 transition-all inline-flex items-center justify-center gap-2 shadow-lg btn-glow">
              Get Started Today
            </button>
            <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer"
              className="px-8 py-3 bg-white/5 text-white border border-white/10 rounded-xl text-sm font-body font-semibold hover:bg-white/8 transition-all inline-flex items-center justify-center gap-2">
              Download App
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
